import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  SMART_GALLERY_NOTICE_FIXTURES,
  SmartGalleryDrawer,
  SmartGalleryPreferences,
  SmartGalleryToast,
} from '../../components/smart/SmartGalleryInbox';
import { SmartShell } from '../../components/smart/SmartShell';
import { Button } from '../../components/smart/ui/Button';
import { FlagIcon } from '../../components/smart/ui/FlagIcon';
import { IconBadge } from '../../components/smart/ui/IconBadge';
import { cn } from '../../components/smart/utils/cn';
import { countryForCurrency } from '../../components/smart/utils/country-meta';
import { formatCurrency } from '../../components/smart/utils/format';
import { SMART_ROUTES } from '../../constants';
import {
  smartMockOperations,
  smartMockRequests,
} from '../../gallery/smartMock';

const OPERATIONS_PAGE_SIZE = 5;

type StatusTone = 'success' | 'warning' | 'neutral';

const toneClasses: Record<StatusTone, string> = {
  success: 'bg-success/15 text-success border-success/30',
  warning: 'bg-warning/15 text-warning border-warning/30',
  neutral: 'bg-bg-muted text-fg-muted border-border',
};

const UNFUNDED_CASH = new Set(['awaiting_funding', 'barcode_issued']);

interface HomePageProps {
  inboxOpen?: boolean;
  toastOpen?: boolean;
  preferencesOpen?: boolean;
}

export const HomePage = ({
  inboxOpen = false,
  toastOpen = false,
  preferencesOpen: preferencesOpenProp = false,
}: HomePageProps = {}) => {
  const { t } = useTranslation('smart');
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'operations' | 'requests'>(
    'operations',
  );
  const [visibleCount, setVisibleCount] = useState(OPERATIONS_PAGE_SIZE);
  const [visibleRequestCount, setVisibleRequestCount] =
    useState(OPERATIONS_PAGE_SIZE);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [noticesOpen, setNoticesOpen] = useState(inboxOpen);
  const [preferencesOpen, setPreferencesOpen] = useState(preferencesOpenProp);
  const [readIds, setReadIds] = useState<string[]>([]);
  const [archivedIds, setArchivedIds] = useState<string[]>([]);

  const operations = smartMockOperations;
  const requests = smartMockRequests;
  const visibleOperations = useMemo(
    () => operations.slice(0, visibleCount),
    [operations, visibleCount],
  );
  const visibleRequests = useMemo(
    () => requests.slice(0, visibleRequestCount),
    [requests, visibleRequestCount],
  );

  const notices = SMART_GALLERY_NOTICE_FIXTURES.filter(
    (item) => !archivedIds.includes(item.id),
  ).map((item) => ({
    id: item.id,
    title: t(item.titleKey),
    body: t(item.bodyKey),
    isRead: item.read || readIds.includes(item.id),
    createdAt: item.createdAt,
  }));
  const unread = notices.filter((item) => !item.isRead).length;

  return (
    <SmartShell
      hideHeader
      variant="home"
      headerMenu={{
        showNotifications: true,
        unread,
        onNotifications: () => setNoticesOpen(true),
      }}
    >
      <section>
        <h1 className="text-3xl leading-tight font-semibold tracking-tight sm:text-4xl">
          {t('home.greeting')}
          <br />
          <span className="text-accent">{t('home.titleAccent')}</span>
        </h1>
        <p className="text-fg-muted mt-3 text-sm">{t('home.subtitle')}</p>
      </section>

      <section className="mt-6">
        <Button
          onClick={() => navigate(SMART_ROUTES.WIZARD_INTENT)}
          fullWidth
          size="lg"
          iconRight={<span aria-hidden>→</span>}
        >
          {t('home.newOperationCta')}
        </Button>
        <p className="text-fg-muted mt-2 text-center text-xs">
          {t('home.newOperationHint')}
        </p>
      </section>

      <section className="mt-8 flex flex-1 flex-col">
        <div
          role="tablist"
          aria-label={t('home.sectionTitle')}
          className="border-border bg-bg-elevated/60 flex gap-1 rounded-xl border p-1"
        >
          {(['operations', 'requests'] as const).map((tab) => {
            const isActive = activeTab === tab;
            const count =
              tab === 'operations' ? operations.length : requests.length;
            return (
              <button
                key={tab}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveTab(tab)}
                className={cn(
                  'flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold transition-colors',
                  isActive
                    ? 'bg-accent text-accent-fg'
                    : 'text-fg-muted hover:text-fg',
                )}
              >
                {t(`home.tabs.${tab}`)}
                {count > 0 && (
                  <span
                    className={cn(
                      'rounded-full px-1.5 py-0.5 text-[10px] font-bold leading-none tabular-nums',
                      isActive
                        ? 'bg-accent-fg/20 text-accent-fg'
                        : 'bg-bg-muted text-fg-muted',
                    )}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {activeTab === 'operations' ? (
          <>
            <ul className="mt-4 flex flex-col gap-3">
              {visibleOperations.map((op) => {
                const isUnfunded = UNFUNDED_CASH.has(op.fundingStatus ?? '');
                const tone: StatusTone = isUnfunded
                  ? 'warning'
                  : op.status === 'completed'
                    ? 'success'
                    : 'neutral';
                const statusLabel = isUnfunded
                  ? t('home.status.send.pending_cash_deposit')
                  : t(`home.opStatus.${op.status}`);
                return (
                  <li key={op.operationId}>
                    <article className="border-border bg-bg-elevated rounded-2xl border p-4">
                      <div className="flex items-start gap-3">
                        <IconBadge size="md" variant="accent" className="mt-0.5">
                          <span className="text-base">↗</span>
                        </IconBadge>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="text-fg text-sm font-semibold leading-tight">
                                {t('home.intentLabel.send')}
                              </p>
                              <p className="text-fg mt-0.5 truncate text-base font-medium leading-tight">
                                {t('home.receives', {
                                  amount: formatCurrency(
                                    op.receiveAmount,
                                    op.destinationCurrency,
                                  ),
                                })}
                              </p>
                            </div>
                            <span
                              className={cn(
                                'shrink-0 whitespace-nowrap rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase leading-none tracking-wide',
                                toneClasses[tone],
                              )}
                            >
                              {statusLabel}
                            </span>
                          </div>
                          <div className="text-fg-muted mt-2 flex items-center gap-2 text-xs">
                            <span className="inline-flex items-center gap-1">
                              <FlagIcon
                                code={countryForCurrency(op.sourceCurrency)}
                                className="text-sm"
                              />
                              <span aria-hidden>→</span>
                              <FlagIcon
                                code={countryForCurrency(op.destinationCurrency)}
                                className="text-sm"
                              />
                            </span>
                            <span aria-hidden>·</span>
                            <span className="text-fg tabular-nums font-semibold">
                              {formatCurrency(op.sendAmount, op.sourceCurrency)}
                            </span>
                          </div>
                        </div>
                      </div>
                      {isUnfunded && (
                        <Button
                          size="sm"
                          fullWidth
                          className="mt-3"
                          onClick={() =>
                            navigate(
                              op.fundingStatus === 'barcode_issued'
                                ? SMART_ROUTES.BARCODE
                                : SMART_ROUTES.MERCHANT_PICKER,
                            )
                          }
                        >
                          {op.fundingStatus === 'barcode_issued'
                            ? t('home.actions.viewBarcode')
                            : t('home.actions.payWithBarcode')}
                        </Button>
                      )}
                    </article>
                  </li>
                );
              })}
            </ul>
            {operations.length > visibleCount && (
              <div className="mt-4">
                <Button
                  variant="secondary"
                  fullWidth
                  onClick={() =>
                    setVisibleCount((c) => c + OPERATIONS_PAGE_SIZE)
                  }
                >
                  {t('home.loadMore', {
                    count: operations.length - visibleCount,
                  })}
                </Button>
              </div>
            )}
          </>
        ) : (
          <>
            <ul className="mt-4 flex flex-col gap-3">
              {visibleRequests.map((request) => {
                const tone: StatusTone =
                  request.status === 'funded' ? 'success' : 'neutral';
                return (
                  <li key={request.requestId}>
                    <article className="border-border bg-bg-elevated rounded-2xl border p-4">
                      <div className="flex items-start gap-3">
                        <IconBadge size="md" variant="accent" className="mt-0.5">
                          <span className="text-base">↙</span>
                        </IconBadge>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="text-fg text-sm font-semibold leading-tight">
                                {t('home.reqIntentLabel')}
                              </p>
                              <p className="text-fg mt-0.5 truncate text-base font-medium leading-tight">
                                {request.amount != null
                                  ? t('home.requested', {
                                      amount: formatCurrency(
                                        request.amount,
                                        request.corridor.destinationCurrency,
                                      ),
                                    })
                                  : t('home.reqIntentLabel')}
                              </p>
                            </div>
                            <span
                              className={cn(
                                'shrink-0 whitespace-nowrap rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase leading-none tracking-wide',
                                toneClasses[tone],
                              )}
                            >
                              {t(`home.reqStatus.${request.status}`)}
                            </span>
                          </div>
                        </div>
                      </div>
                      {request.status === 'pending' && request.fundRequestUrl && (
                        <Button
                          size="sm"
                          variant="secondary"
                          fullWidth
                          className="mt-3"
                          onClick={() => {
                            void navigator.clipboard?.writeText(
                              request.fundRequestUrl!,
                            );
                            setCopiedId(request.requestId);
                            setTimeout(() => setCopiedId(null), 1500);
                          }}
                        >
                          {copiedId === request.requestId
                            ? t('home.actions.copied')
                            : t('home.actions.copyLink')}
                        </Button>
                      )}
                    </article>
                  </li>
                );
              })}
            </ul>
            {requests.length > visibleRequestCount && (
              <div className="mt-4">
                <Button
                  variant="secondary"
                  fullWidth
                  onClick={() =>
                    setVisibleRequestCount((c) => c + OPERATIONS_PAGE_SIZE)
                  }
                >
                  {t('home.requestsLoadMore', {
                    count: requests.length - visibleRequestCount,
                  })}
                </Button>
              </div>
            )}
          </>
        )}
      </section>

      <SmartGalleryDrawer
        items={notices}
        open={noticesOpen}
        onClose={() => setNoticesOpen(false)}
        onMarkRead={(id) => setReadIds((prev) => [...prev, id])}
        onArchive={(id) => setArchivedIds((prev) => [...prev, id])}
        onOpenPreferences={() => {
          setNoticesOpen(false);
          setPreferencesOpen(true);
        }}
      />
      <SmartGalleryPreferences
        open={preferencesOpen}
        onClose={() => setPreferencesOpen(false)}
      />
      {toastOpen ? (
        <SmartGalleryToast message={t('notifications.exampleToast')} />
      ) : null}
    </SmartShell>
  );
};

export const InboxPage = () => <HomePage inboxOpen />;
export const PreferencesPage = () => <HomePage preferencesOpen />;
export const ToastPage = () => <HomePage toastOpen />;
